import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Link,
  Stack,
  Pagination
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import instance from '../../utils/axiosConfig';

function PendingInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [approveDialog, setApproveDialog] = useState({
    open: false,
    invoice: null
  });
  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    invoice: null
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async (page = 1) => {
    try {
      const response = await instance.get(`/api/moderator-management/pending-invoices?page=${page}`);
      setInvoices(response.data.invoices);
      setPagination(response.data.pagination);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al cargar facturas pendientes'
      });
    }
  };

  const handleApproveClick = (invoice) => {
    setApproveDialog({
      open: true,
      invoice: invoice
    });
  };

  const handleApproveCancel = () => {
    setApproveDialog({
      open: false,
      invoice: null
    });
  };

  const handleApproveConfirm = async () => {
    try {
      await instance.post(`/api/moderator-management/approve-invoice/${approveDialog.invoice._id}`);
      setMessage({ type: 'success', text: 'Factura aprobada exitosamente' });
      fetchInvoices();
      handleApproveCancel();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al aprobar factura'
      });
    }
  };

  const handleRejectClick = (invoice) => {
    setRejectDialog({
      open: true,
      invoice: invoice
    });
  };

  const handleRejectCancel = () => {
    setRejectDialog({
      open: false,
      invoice: null
    });
  };

  const handleRejectConfirm = async () => {
    try {
      await instance.post(`/api/moderator-management/reject-invoice/${rejectDialog.invoice._id}`);
      setMessage({ type: 'success', text: 'Factura rechazada exitosamente' });
      fetchInvoices(pagination.currentPage);
      handleRejectCancel();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al rechazar factura'
      });
    }
  };

  return (
    <Box>
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Información del Distribuidor</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Meses</TableCell>
              <TableCell>Monto</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Comprobante</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow key={invoice._id}>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {invoice.user?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Razón Social: {invoice.user?.governmentName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      CUIT: {invoice.user?.CUIT}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Email: {invoice.user?.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Teléfono: {invoice.user?.phone}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Dirección: {invoice.user?.address}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MilitaryTechIcon 
                      sx={{ 
                        color: invoice.planId?.title === 'plata' ? '#C0C0C0' : invoice.planId?.title === 'oro' ? '#FFD700' : invoice.planId?.title === 'diamante' ? '#42e3e3' : 'error.main',
                        fontSize: '2.2rem'
                      }} 
                    />
                  </Box>
                </TableCell>
                <TableCell>{invoice.planMonths}</TableCell>
                <TableCell>${invoice.planAmount}</TableCell>
                <TableCell>
                  {new Date(invoice.dateUploaded).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Chip 
                    label="Pendiente" 
                    color="warning" 
                  />
                </TableCell>
                <TableCell>
                  <Link 
                    href={invoice.invoiceUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <IconButton
                      color="primary"
                      title="Ver comprobante"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Link>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      onClick={() => handleApproveClick(invoice)}
                      color="success"
                      title="Aprobar factura"
                    >
                      <CheckCircleIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleRejectClick(invoice)}
                      color="error"
                      title="Rechazar factura"
                    >
                      <CancelIcon />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {invoices.length === 0 && (
        <Typography sx={{ mt: 2, textAlign: 'center' }}>
          No hay facturas pendientes
        </Typography>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={approveDialog.open}
        onClose={handleApproveCancel}
      >
        <DialogTitle>Confirmar Aprobación</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            ¿Estás seguro que deseas aprobar la factura del siguiente distribuidor?
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Nombre:</strong> {approveDialog.invoice?.user?.name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Razón Social:</strong> {approveDialog.invoice?.user?.governmentName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>CUIT:</strong> {approveDialog.invoice?.user?.CUIT}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Plan:</strong> {approveDialog.invoice?.planId?.title.toUpperCase()}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Meses:</strong> {approveDialog.invoice?.planMonths}
            </Typography>
            <Typography variant="body2">
              <strong>Monto:</strong> ${approveDialog.invoice?.planAmount}
            </Typography>
          </Box>
          <Typography variant="body2" color="warning.main">
            Esta acción asignará automáticamente el plan al distribuidor.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleApproveCancel}>
            Cancelar
          </Button>
          <Button onClick={handleApproveConfirm} color="success" variant="contained">
            Aprobar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialog.open}
        onClose={handleRejectCancel}
      >
        <DialogTitle>Confirmar Rechazo</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            ¿Estás seguro que deseas rechazar la factura del siguiente distribuidor?
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Nombre:</strong> {rejectDialog.invoice?.user?.name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Razón Social:</strong> {rejectDialog.invoice?.user?.governmentName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>CUIT:</strong> {rejectDialog.invoice?.user?.CUIT}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Plan:</strong> {rejectDialog.invoice?.planId?.name}
            </Typography>
            <Typography variant="body2">
              <strong>Monto:</strong> ${rejectDialog.invoice?.planAmount}
            </Typography>
          </Box>
          <Typography variant="body2" color="error">
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectCancel}>
            Cancelar
          </Button>
          <Button onClick={handleRejectConfirm} color="error" variant="contained">
            Rechazar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pagination */}
      {invoices.length > 0 && (
        <Stack 
          direction="row" 
          justifyContent="center" 
          alignItems="center" 
          spacing={2}
          sx={{ mt: 3 }}
        >
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={(e, page) => fetchInvoices(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
    </Box>
  );
}

export default PendingInvoices; 